import { useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Link from "../../components/Common/Link";
import styled from "styled-components";

import theme from "../../styles/theme";
import Achievements from "./Achievements";
import { useDriverContext } from "../../contexts/driver";
import NoneDriver from "../../assets/none_driver.png";
// import SocialMediaLinks from "../../components/Driver/SocialMediaLinks";
import { isDark } from "../../utils/helpers/colors";
import DriverStats from "../../components/Driver/DriverStats";
import DriverOverview from "../../components/Driver/DriverOverview";
import DriverProfileSkeleton from "../../components/Driver/DriverProfileSkeleton";
import DriverSEO from "../../components/SEO/DriverSEO";
import NotFound from "../NotFound";
import ProvisionalMark from "../../components/Elo/ProvisionalMark";

const BlockTitle = styled.div`
  font-size: 32px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  border-bottom: 8px solid ${(props) => props.borderColor || theme.colors.green};
  margin-bottom: 20px;
  padding-bottom: 0;
  width: 100%;
  display: flex;
  justify-content: row;
  align-items: end;

  @media (max-width: ${theme.breakpointsCustom.md}px) {
    margin-bottom: 0;
  }
`;

const MobileTabsContainer = styled.div`
  display: none;
  width: 100%;
  position: relative;
  overflow: hidden;

  @media (max-width: ${theme.breakpointsCustom.md}px) {
    display: flex;
    margin-bottom: 20px;
  }
`;

const MobileTabSlider = styled.div`
  position: absolute;
  height: 100%;
  width: 50%;
  background-color: ${(props) => props.borderColor || theme.colors.green};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  transition: transform 0.3s ease;
  transform: translateX(${(props) => (props.activeTab === "overview" ? "0" : "100%")});
  z-index: 1;
`;

const MobileTabButton = styled.button`
  position: relative;
  width: 50%;
  height: 32px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: transparent;
  outline: none;
  border: none;
  color: ${(props) => (props.active ? props.textColor || "black" : theme.colors.white)};
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 2;

  &:hover {
    color: ${(props) =>
      !props.active ? props.borderColor || theme.colors.green : props.textColor || "black"};
  }
`;

const ImageContainer = styled.div`
  max-width: 320px;
  width: 100%;
  text-align: center;
  height: 150px;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    max-width: none;
    width: fit-content;
    height: 100%;
    display: flex;
  }
`;

const StyledImage = styled.img`
  width: 150px;
  height: 150px;
  object-fit: cover;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    width: 120px;
    height: 120px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const DriverName = styled.h1`
  margin-top: 0;
  margin-bottom: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 36px;
  font-weight: bold;
  margin-left: 16px;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    font-size: 26px;
    margin-left: 16px;
  }
`;

const ButtonsTitleContainer = styled.div`
  display: flex;
  align-items: end;
`;

const TabSlider = styled.div`
  position: absolute;
  height: 32px;
  width: 100px;
  background-color: ${(props) => props.borderColor || theme.colors.green};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  transition: transform 0.3s ease;
  transform: translateX(${(props) => (props.activeTab === "overview" ? "0" : "100px")});
  z-index: 1;

  @media (max-width: ${theme.breakpointsCustom.md}px) {
    display: none;
  }
`;

const TabButton = styled.button`
  position: relative;
  width: 100px;
  height: 32px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: transparent;
  outline: none;
  border: none;
  color: ${(props) => (props.active ? props.textColor || "black" : theme.colors.white)};
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 2;

  &:hover {
    color: ${(props) =>
      !props.active ? props.borderColor || theme.colors.green : props.textColor || "black"};
  }

  @media (max-width: ${theme.breakpointsCustom.md}px) {
    display: none;
  }
`;

const MainContainer = styled.div`
  display: flex;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
  }
`;

const LeftSideContainer = styled.div`
  background: #2a2a2ac4;
  max-width: 320px;
  height: fit-content;
  width: 100%;
  border: 2px solid ${theme.colors.secondaryDark};
  padding: 16px 16px 16px 0px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    width: 100%;
    max-width: none;
    display: ${(props) => (props.activeTab === "overview" ? "flex" : "none")};
  }
`;

const RowContainer = styled.div`
  border-bottom: 2px solid ${theme.colors.secondaryDark};
  padding-bottom: 8px;
`;

const DriverTeam = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  font-weight: bold;
  padding-left: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DefaultRowSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 32px;
  padding-top: 4px;
`;

const DefaultRowSectionBlock = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SmallerTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;
`;

const Quantity = styled.div`
  color: #f58020;
  font-size: 12px;
  font-weight: bold;
  font-family: "Montserrat", sans-serif;
  color: ${(props) => props.color || theme.colors.green};
`;

const RightSideContainer = styled.div`
  margin-left: 16px;
  width: 100%;
  position: relative;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    margin-left: 0px;
  }
`;

const TeamIconImage = styled.img`
  width: 24px;
  height: 24px;
`;
const TeamContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const TabContent = styled.div`
  display: ${(props) => (props.active ? "block" : "none")};
  width: 100%;
`;

const DriverProfile = () => {
  const {
    setSelectedDriver,
    driverDetails,
    resetDriverContext,
    isDetailsLoading,
    isStatsLoading,
    isEventsLoading,
    setSelectedSimulatorId,
  } = useDriverContext();
  const { id, seoUrl } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get("tab") === "statistics" ? "statistics" : "overview",
  );

  useEffect(() => {
    if (id) {
      setSelectedDriver({ id });
    }
    return () => {
      resetDriverContext();
    };
  }, [id, setSelectedDriver, resetDriverContext]);

  // A `simulator` query param shows the same per-simulator figures the link came from.
  useEffect(() => {
    const simulatorParam = searchParams.get("simulator");
    if (simulatorParam) {
      setSelectedSimulatorId(Number(simulatorParam));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (driverDetails && driverDetails.seoUrl) {
      if (!seoUrl) {
        navigate(`/drivers/${id}/${driverDetails.seoUrl}`, { replace: true });
      } else if (seoUrl !== driverDetails.seoUrl) {
        navigate(`/drivers/${id}/${driverDetails.seoUrl}`, { replace: true });
      }
    }
  }, [driverDetails, id, seoUrl, navigate]);

  const driverFullName = useMemo(() => {
    if (!driverDetails) return "null";
    return `${driverDetails.firstName} ${driverDetails.lastName}`;
  }, [driverDetails]);

  if (isDetailsLoading || isStatsLoading || isEventsLoading) {
    return <DriverProfileSkeleton />;
  }

  // An unknown id is a 404 like any other: /drivers/999999 answers 200 and used to render a bare
  // indexable div. NotFound carries the noindex and the links back. DriverSEO only renders below,
  // in the success path, so there is no competing robots tag.
  if (!driverDetails) {
    return (
      <NotFound
        title="Пілота не знайдено"
        text="Такого пілота немає в базі Slick Racing Ukraine — можливо, в посиланні є помилка. Перегляньте повний список пілотів."
      />
    );
  }

  // const socialNetworks = driverDetails.socialNetworks || [];
  const { driversAchievements, constructorAchievements } = driverDetails;
  const team = driverDetails.team || [];
  const buttonColor = driverDetails.team?.color || "white";
  const textColor = isDark(buttonColor) ? "white" : "black";
  const currentUrl = `https://slickracing.com.ua/drivers/${id}/${driverDetails?.seoUrl || ""}`;

  return (
    <>
      <DriverSEO driver={driverDetails} url={currentUrl} />
      <div style={{ marginTop: 24 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link style={{ underline: "hover", color: "inherit" }} to="/">
            Головна
          </Link>
          <Link style={{ underline: "hover", color: "inherit" }} to="/drivers">
            Пілоти
          </Link>
          <Typography sx={{ color: "#c1c1c1", cursor: "default" }}>{driverFullName}</Typography>
        </Breadcrumbs>
        <div style={{ marginTop: 24 }}>
          <BlockTitle borderColor={driverDetails.team?.color}>
            <ImageContainer>
              <StyledImage src={driverDetails.image || NoneDriver} alt={driverFullName} />
            </ImageContainer>
            <TitleContainer>
              <DriverName>{driverFullName}</DriverName>
              <ButtonsTitleContainer>
                <TabSlider borderColor={driverDetails.team?.color} activeTab={activeTab} />
                <TabButton
                  borderColor={driverDetails.team?.color}
                  textColor={textColor}
                  active={activeTab === "overview"}
                  onClick={() => setActiveTab("overview")}
                >
                  <div style={{ fontSize: 14 }}>Огляд</div>
                </TabButton>
                <TabButton
                  borderColor={driverDetails.team?.color}
                  textColor={textColor}
                  active={activeTab === "statistics"}
                  onClick={() => setActiveTab("statistics")}
                >
                  <div style={{ fontSize: 14 }}>Статистика</div>
                </TabButton>
              </ButtonsTitleContainer>
            </TitleContainer>
          </BlockTitle>

          <MobileTabsContainer>
            <MobileTabSlider borderColor={driverDetails.team?.color} activeTab={activeTab} />
            <MobileTabButton
              borderColor={driverDetails.team?.color}
              textColor={textColor}
              active={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Огляд
            </MobileTabButton>
            <MobileTabButton
              borderColor={driverDetails.team?.color}
              textColor={textColor}
              active={activeTab === "statistics"}
              onClick={() => setActiveTab("statistics")}
            >
              Статистика
            </MobileTabButton>
          </MobileTabsContainer>

          <MainContainer>
            <LeftSideContainer activeTab={activeTab}>
              {team && Object.keys(team).length > 0 && (
                <RowContainer>
                  <DriverTeam>
                    <Title>Команда</Title>
                    <TeamContainer>
                      {team.logoImage && <TeamIconImage src={team.logoImage} alt={team.name} />}
                      <SmallerTitle>{team.name}</SmallerTitle>
                    </TeamContainer>
                  </DriverTeam>
                </RowContainer>
              )}
              {/* {socialNetworks.length > 0 && (
                <RowContainer>
                  <SocialMediaLinks socialNetworks={socialNetworks} />
                </RowContainer>
              )} */}

              <RowContainer>
                <Title>Рейтинг</Title>
                <DefaultRowSectionContainer>
                  {driverDetails.rating?.elo && (
                    <DefaultRowSectionBlock>
                      <SmallerTitle>Slick Rating</SmallerTitle>
                      <Quantity color={driverDetails.team?.color}>
                        {driverDetails.rating.elo}
                        {driverDetails.rating.provisional && (
                          <ProvisionalMark title="Рейтинг пілота калібрується." />
                        )}
                      </Quantity>
                    </DefaultRowSectionBlock>
                  )}
                  <DefaultRowSectionBlock>
                    <SmallerTitle>Safety points</SmallerTitle>
                    <Quantity color={driverDetails.team?.color}>
                      {driverDetails.rating?.safetyPoints}
                    </Quantity>
                  </DefaultRowSectionBlock>
                </DefaultRowSectionContainer>
              </RowContainer>
              <RowContainer>
                <Title>Історія</Title>
                <DefaultRowSectionContainer>
                  {driverDetails.racingHistory.map((item) => {
                    return (
                      <>
                        <SmallerTitle style={{ marginLeft: "auto" }}>{item.simulator}</SmallerTitle>
                        <DefaultRowSectionBlock>
                          <SmallerTitle>Гонки</SmallerTitle>
                          <Quantity color={driverDetails.team?.color}>
                            {item.grandPrixCount || 0}
                          </Quantity>
                        </DefaultRowSectionBlock>
                        <DefaultRowSectionBlock>
                          <SmallerTitle>Очки</SmallerTitle>

                          <Quantity color={driverDetails.team?.color}>{item.points || 0}</Quantity>
                        </DefaultRowSectionBlock>
                      </>
                    );
                  })}
                </DefaultRowSectionContainer>
              </RowContainer>
              <RowContainer style={{ borderBottom: "none" }}>
                <Achievements
                  driverAchievements={driversAchievements}
                  teamAchievements={constructorAchievements}
                />
              </RowContainer>

              {/* {awards.length > 0 && (
                <RowContainer style={{ borderBottom: "none" }}>
                  <Title>Нагороди</Title>
                  <BonusAccordions awards={awards} team={team} />
                </RowContainer>
              )} */}
            </LeftSideContainer>

            <RightSideContainer>
              <TabContent active={activeTab === "overview"}>
                <DriverOverview driverDetails={driverDetails} driverFullName={driverFullName} />
              </TabContent>
              <TabContent active={activeTab === "statistics"}>
                {/* The simulator dropdown lives inside DriverStats, on the one section it scopes. */}
                <DriverStats driverId={id} color={driverDetails.team?.color} />
              </TabContent>
            </RightSideContainer>
          </MainContainer>
        </div>
      </div>
    </>
  );
};

export default DriverProfile;
